import { z } from 'zod';
import { createModel, partySchema } from '../../common';
import { dateSchema, idSchema, objectIdSchema } from '../../validation';
import { appIdSchema } from './app.model';

/** Provider operation a log row represents. */
export const appLogOperationSchema = z.enum([
  'verify_credentials',
  'create_trip',
  'update_trip',
  'update_waybill',
  'close_waybill',
  'cancel_waybill',
  'get_trip',
  'get_trip_pdf',
  'lookup_good_types',
]);
export type AppLogOperation = z.infer<typeof appLogOperationSchema>;

/**
 * Outcome of one attempt.
 *
 * The distinction that matters is `retryable` vs `failed`. A 4xx from the
 * provider means the payload is wrong — retrying it forever burns quota and
 * never succeeds, so it lands as `failed` and waits for a human. Network
 * errors, timeouts, and 5xx land as `retryable` and the cron picks them up.
 */
export const appLogStatusSchema = z.enum(['success', 'retryable', 'failed', 'skipped']);
export type AppLogStatus = z.infer<typeof appLogStatusSchema>;

/**
 * `AppLog db model` — audit row for one interaction with an app's provider.
 *
 * Doubles as the retry queue. The backend has no Bull/BullMQ; scheduled work
 * runs on `@nestjs/schedule`, so `AppRetryCron` sweeps rows where
 * `status === 'retryable'` and `nextRetryAt <= now`. Persisting the queue in
 * Mongo rather than memory also means a deploy mid-retry doesn't drop work.
 *
 * Request and response bodies are stored verbatim because government
 * submissions get disputed months later and "what exactly did we send" is the
 * only answer that settles it. They are NOT redacted: the payload contains no
 * secrets (the account identifier travels in a header, which is excluded).
 */
export const AppLogSchema = createModel({
  appId: appIdSchema,

  operation: appLogOperationSchema,

  status: appLogStatusSchema,

  /**
   * The party the call was made for — the same `owner` the {@link AppInstalled}
   * row carries.
   *
   * An `org` for a company, a `driver` for an owner-operator who has no Org
   * anywhere. It was called `org` and typed as a Party, which read as "this is
   * always a company" and made every driver-filed row look like a mistake. The
   * Party's `ids[]` carries the login behind a driver as a `user_id` entry, so
   * the audit trail can be walked from either direction.
   */
  owner: partySchema,

  /** `AppInstalled` `_id` hex string. */
  installedId: idSchema.optional(),

  /**
   * The `Order` this run was for, when it was order-scoped.
   *
   * A real ObjectId, not a hex string: `AppRetryCron` reads it straight back to
   * replay the operation, and the admin viewer joins on it. Stored as a string
   * it could neither be `$lookup`-ed nor compared against `Order._id`.
   */
  orderId: objectIdSchema.optional(),

  /** Request body sent to the provider, headers excluded. */
  request: z.unknown().optional(),

  /** Parsed response body, or the raw error payload on failure. */
  response: z.unknown().optional(),

  /** Provider HTTP status. Absent when the call never reached the provider. */
  httpStatus: z.number().optional(),

  /** Human-readable error for the UI. */
  error: z.string().optional(),

  /**
   * Stable error code for branching (e.g. `bayan_not_configured`,
   * `missing_carrier_moi`, `city_unmapped`, `naql_license_invalid`).
   * Frontend maps these to localized messages.
   */
  errorCode: z.string().optional(),

  /** Attempts made so far, including the first. */
  attempts: z.number().default(1),

  /** When the cron should try again. Only set while `status` is `retryable`. */
  nextRetryAt: dateSchema.optional(),

  /** Wall-clock duration of the provider call, for latency tracking. */
  durationMs: z.number().optional(),
});

export type AppLog = z.infer<typeof AppLogSchema.model>;
export type AppLogData = z.infer<typeof AppLogSchema.data>;
export type AppLogPartial = z.infer<typeof AppLogSchema.partial>;
